import { createContext, use, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LocationDTO, MeDTO } from '@lightbm/shared';
import { useAuth } from '@/features/auth/auth-context';
import { api } from '@/lib/api-client';
import { STORE_KEYS, localStore } from '@/lib/local-store';
import { queryKeys } from '@/lib/query-client';

interface LocationState {
  /** Sites the user can work in (USR-08): the switcher and user access offer these. */
  locations: LocationDTO[];
  /** The same sites with their zones, shelves and bins (pickers of operations). */
  places: LocationDTO[];
  /** null means "All locations". */
  selectedId: string | null;
  selected: LocationDTO | null;
  select: (id: string | null) => void;
}

const LocationContext = createContext<LocationState | null>(null);

function UserLocationProvider({ user, children }: { user: MeDTO | null; children: ReactNode }) {
  const { data: places = [] } = useQuery({
    queryKey: queryKeys.myLocations,
    queryFn: () => api.get<LocationDTO[]>('/locations'),
    enabled: Boolean(user) && !user?.mustChangePassword,
    staleTime: 5 * 60_000,
  });

  const locations = places.filter((location) => location.parentId === null);

  // Last choice on this device, else the user's default location (USR-09), else all locations.
  const [preferred, setPreferred] = useState<string | null>(() =>
    user
      ? (localStore.get(STORE_KEYS.location(user.id)) ?? user.defaultLocation?.id ?? null)
      : null,
  );
  const selected = locations.find((location) => location.id === preferred) ?? null;

  const select = (id: string | null) => {
    setPreferred(id);
    if (user) localStore.set(STORE_KEYS.location(user.id), id);
  };

  return (
    <LocationContext
      value={{ locations, places, selectedId: selected?.id ?? null, selected, select }}
    >
      {children}
    </LocationContext>
  );
}

/** The "All locations" switcher of the top bar; operations of later phases pre-fill from it. */
export function LocationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // A new user starts from their own stored choice.
  return (
    <UserLocationProvider key={user?.id ?? 'signed-out'} user={user}>
      {children}
    </UserLocationProvider>
  );
}

/** Active places in tree order, named "Site" or "Site › Zone": where limits are set (SL-05). */
export function placeTree(places: LocationDTO[]): Array<{ id: string; name: string }> {
  const open = places.filter((place) => !place.isSystem && place.isActive);
  return open
    .filter((place) => place.parentId === null)
    .flatMap((site) => [
      { id: site.id, name: site.name },
      ...open
        .filter((place) => place.parentId === site.id)
        .map((place) => ({ id: place.id, name: `${site.name} › ${place.name}` })),
    ]);
}

export function useLocationScope(): LocationState {
  const context = use(LocationContext);
  if (!context) throw new Error('useLocationScope must be used inside LocationProvider');
  return context;
}
